import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import styled from "styled-components";
import Year from "@/components/Archive/Year";
import useEmblaCarousel from "embla-carousel-react";
import {clamp, getCssSizeValue, getMapKey} from "@/utils/utils";
import {ScrollTrigger, useGSAP} from "@/utils/gsap";
import {useIsMobile} from "@/hooks/useDevice";
import {useArchiveContext} from "@/components/Archive/index";
import Counter from "@/components/Archive/Counter";
import {useViewportHeight} from "@/hooks/useViewport";
import useScrollContext from "@/hooks/useScrollContext";
import {BREAKPOINTS} from "@/styles/themeSettings";

const MainContainer = styled.div`
    position: relative;
    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.TABLET]}px) {
		height: ${({ $height }) => `${$height || 1}00svh`};
    }
`

const Wrapper = styled.div`
	position: relative;
	display: flex;

    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.TABLET]}px) {
        height: 100svh;
    }
	
    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
        overflow: hidden;
    }
`

const Container = styled.div`
	display: flex;
	width: 100%;
	
    @media (max-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.TABLET] - 1}px) {
		gap: ${getCssSizeValue(150, BREAKPOINTS.MOBILE)};
        flex-direction: column;
    }
`

const TimeSpan = forwardRef(function TimeSpan({ data }, fref){
	const [years, date] = useMemo(() => {
		const source = data?.years || []
		const date = data?.date || 'Year'
		const context = data?.context
		return [[{ date, context }, ...source], date]
	}, [data])
	const [yearIndex, setYearIndex] = useState(0)
	const MainRef = useRef()
	const WrapperRef = useRef()
	const isMobile = useIsMobile()
	const setCurrent = useArchiveContext('setCurrent')
	const viewportHeight = useViewportHeight()
	const scroll = useScrollContext()
	const [EmblaRef, EmblaApi] = useEmblaCarousel( {
		loop: false,
		align: 'start',
		watchDrag: false,
		active: !isMobile
	})

	useImperativeHandle(fref, () => MainRef.current, [])

	const handleOnSlideIndicatorClick = useCallback((index) => {
		const { top } = MainRef.current.getBoundingClientRect()
		const offset = index * viewportHeight
		const scrollProps = scroll.props()
		const target = top + scrollProps.scroll + offset
		// alert(`target:${target}, viewportHeight:${viewportHeight}`)
		scroll.scrollTo({
			top: target,
			behavior: 'smooth'
		})
	},[scroll, viewportHeight])

	useEffect(() => {
		if(EmblaApi){
			EmblaApi.scrollTo(yearIndex)
		}
	}, [EmblaApi, yearIndex]);

	useGSAP(() => {

		const length = years.length - 1
		// const end = undefined
		ScrollTrigger.create({
			trigger: MainRef.current,
			// markers: true,
			end: 'bottom bottom',
			// end,
			pin: !isMobile ? WrapperRef.current : false,
			// pin: WrapperRef.current ,
			invalidateOnRefresh: true,
			pinSpacing: false,
			onEnter: () => {
				setCurrent(date)
			},
			onEnterBack: () => {
				setCurrent(date)
			},
			onUpdate: ({ progress }) => {
				if(isMobile) return
				const currentYearIndex = clamp(Math.round(progress * length), 0, length)
				setYearIndex(currentYearIndex)
			}
		})
	},{
		dependencies:[years, isMobile, setCurrent, date],
		scope: MainRef.current,
		revertOnUpdate: true
	})

	return (
		<MainContainer ref={MainRef} $height={years.length} className="col-mobile-8 col-tablet-8 col-desktop-16">
			<Wrapper
				ref={(ref) => {
					EmblaRef(ref)
					WrapperRef.current = ref
				}}
			>
				<Container>
					{years.map((year,index) => (
						<Year
							key={getMapKey(year, index)}
							data={year}
							active={yearIndex === index}
						/>
					))}
				</Container>
				{!isMobile && (
					<Counter
						data={years}
						currentIndex={yearIndex}
						onIndicatorClick={handleOnSlideIndicatorClick}
					/>
				)}
			</Wrapper>
		</MainContainer>
	);
});

export default TimeSpan;